import React, { useRef, useState, useEffect } from 'react'
import {
	InstantSearch,
	SortBy,
	Pagination,
	ClearRefinements,
	HitsPerPage,
	Configure,
	SearchBox,
	connectHits,
} from 'react-instantsearch-dom'
import algoliasearch from 'algoliasearch/lite'
import { useStaticQuery, graphql, Link } from 'gatsby'
import tw, { css, styled } from 'twin.macro'

import {
	Search,
	X,
	List,
	DollarSign,
	Tag,
	Hash,
	RefreshCw,
	ChevronLeft,
	ChevronRight,
} from 'react-feather'
/** Page components */
import Layout from '../../components/layout'
import Meta from '../../components/meta'
import Hits from '../../components/shop/hits'
import {
	ClearFiltersMobile,
	SaveFiltersMobile,
	ResultsNumberMobile,
	AttributeSelect,
	BrandSelect,
	CategoryTree,
} from '../../components/shop/widgets'
import withURLSync from '../../components/shop/URLSync'

const searchClient = algoliasearch(
	'7WJPSAMHA3',
	process.env.GATSBY_ALGOLIA_SEARCH_KEY
)
/** Styles with Emotion & Tailwindcss */
const Header = styled.header`
	background: linear-gradient(to bottom, #fbc300, #c98a00);
	background: linear-gradient(
			to bottom,
			rgba(0, 0, 0, 0.075),
			rgba(0, 0, 0, 0.125) 80%,
			rgba(0, 0, 0, 0.25)
		),
		url('https://images.unsplash.com/photo-1504309092620-4d0ec726efa4?fit=crop&w=1950&h=525&q=60');
	min-height: 200px;
	${tw`flex flex-col justify-center items-center bg-left-bottom bg-cover text-white text-center px-2 py-4`}
	p.title {
		@media (min-width: 525px) {
			${tw`mt-4`}
		}
		${tw`text-4xl font-light mt-0 sm:mt-6 md:mt-12`}
		text-shadow: 0px 0px 2px rgba(0,0,0,.625);
	}
	.ais-SearchBox {
		${tw`h-16 w-full md:w-3/4 my-6 md:my-12 rounded-md shadow-xl`}
		form[role='search'] {
			${tw`relative`}
			input[type='search'] {
				${tw`bg-white rounded-md shadow h-16 pt-1 pr-12 pb-0 pl-20 w-full border-0 outline-none text-gray-600 text-sm font-extralight appearance-none`}
				&::placeholder {
					color: rgba(33, 36, 61, 0.24);
					opacity: 1; /* Firefox */
				}
				&:-ms-input-placeholder {
					color: rgba(33, 36, 61, 0.24);
				}
				&::-ms-input-placeholder {
					color: rgba(33, 36, 61, 0.24);
				}
			}
			button[type='submit'] {
				${tw`absolute flex items-center justify-center h-full pt-0 pr-4 pb-0 pl-8 w-20 text-yellow-500 top-0 left-0 appearance-none`}
			}
			button[type='reset'] {
				${tw`absolute flex items-center justify-center h-full p-0 w-12 text-gray-800 top-0 right-0 appearance-none`}
			}
		}
	}
	@media (max-width: 899px) {
		min-height: 250px;
		background: linear-gradient(to bottom, #ae8600, #885b01);
		background: linear-gradient(
				to bottom,
				rgba(0, 0, 0, 0.1),
				rgba(0, 0, 0, 0.3)
			),
			url('https://images.unsplash.com/photo-1504309092620-4d0ec726efa4?fit=crop&w=1950&h=525&q=60');
		background-position: bottom;
		background-size: cover;
		position: relative;
		transition: transform 0.2s ease-out;
		div.search-box {
			${tw`pt-6 bottom-0 left-0 absolute transform translate-y-1/2 shadow-none`}
			form[role='search'] {
				max-width: 90%;
				${tw` mx-auto`}
				input[type='search'] {
					${tw`text-base`}
				}
				button[type='submit'] {
					top: 50%;
					${tw`absolute flex items-center justify-center h-full pt-0 pr-4 pb-0 pl-8 w-20 text-yellow-500 left-0 transform -translate-y-1/2 appearance-none`}
				}
				button[type='reset'] {
					top: 50%;
					${tw`absolute flex items-center justify-center h-full p-0 w-12 text-gray-800 right-0 transform -translate-y-1/2 appearance-none`}
				}
			}
		}
	}
`
const Wrapper = styled.div`
	${tw`bg-gray-50`}
	@media (min-width: 900px) {
		[data-layout='mobile'] {
			display: none !important;
		}
	}
	@media (max-width: 899px) {
		/* Hide all desktop-specific design on mobile */
		[data-layout='desktop'] {
			display: none !important;
		}
	}
	em {
		font-style: normal;
	}
	em,
	mark {
		background: rgba(226, 164, 0, 0.4);
	}
`
const Dashboard = styled.main`
	${tw`flex`}
`
const Filters = styled.section`
	${tw`flex flex-1 flex-col border-b border-r border-gray-200 bg-white`}
	max-width: 260px;
	h2 {
		${tw`text-xl`}
		.clear-filters {
			${tw`flex items-center ml-auto text-sm`}
			button {
				${tw`flex items-center bg-yellow-500 text-gray-800 rounded px-1`}
				svg {
					${tw`mr-2 text-yellow-800`}
				}
				&[disabled] {
					${tw`cursor-not-allowed bg-gray-200 text-gray-500`}
					svg {
						${tw`mr-2 text-gray-500`}
					}
				}
			}
		}
	}
	nav {
		.label {
			${tw`flex items-center px-2 py-2 text-sm leading-5 font-medium text-gray-600 mt-4 mb-2 uppercase`}
			> svg {
				${tw`mr-3 h-4 w-4 text-yellow-500`}
			}
			&:first-of-type {
				${tw`mt-0`}
			}
		}
	}
	@media (max-width: 899px) {
		position: fixed;
		top: 4rem;
		right: 0;
		bottom: 0;
		left: 0;
		max-width: initial;
		padding-bottom: 9rem;
		transform: translateY(120vh);
		transition: transform 0.3s cubic-bezier(0.465, 0.183, 0.153, 0.946);
		width: 100%;
		will-change: transform;
		z-index: 1;
		nav {
			overflow-y: auto;
			padding-bottom: 2rem;
		}
		&[data-show='true'] {
			transform: translateY(4rem);
		}
	}
`
const FiltersFooter = styled.footer`
	@media (max-width: 899px) {
		display: none;
		&[data-show='true'] {
			background-color: #fff;
			border-top: 1px solid #ebecf3;
			bottom: 0;
			display: flex;
			justify-content: space-between;
			margin: 0;
			padding: 1.25rem;
			position: fixed;
			width: 100%;
			z-index: 5;
		}
		.container-filters-footer-button-wrapper {
			width: calc(50% - 0.5rem);
		}
		.ais-ClearRefinements-button,
		.button {
			${tw`flex justify-center bg-gray-200 border-0 rounded-md text-gray-600 text-sm font-semibold p-3 text-center cursor-pointer w-full`}
			&.button-primary {
				${tw`bg-yellow-500 text-gray-800`}
			}
		}
	}
`
const Board = styled.div`
	${tw`flex flex-col w-0 flex-1`}
	@media (max-width: 899px) {
		${tw`pt-10`}
	}
	main {
		${tw`flex-1 relative z-0 pt-2 pb-6 focus:outline-none md:py-6`}
		.menu-bar {
			${tw`max-w-7xl mx-auto px-4 sm:px-6 md:px-8`}

			.ais-SortBy,.ais-HitsPerPage {
				${tw`flex justify-start items-center ml-2 md:ml-4 relative cursor-pointer`}
				select {
					${tw`bg-transparent font-light text-sm text-gray-800 appearance-none border-0 cursor-pointer`}
				}
				&::after {
					${tw`inline-flex pb-1`}
					content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='7' viewBox='0 0 12 7'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath d='M14-5v16H-2V-5z'/%3E%3Cpath fill='%23000' stroke='%23FFF' stroke-width='.5' d='M2.228 1.332a.664.664 0 0 0-.942.001.665.665 0 0 0-.002.941l4.247 4.247c.259.26.679.26.938 0l4.247-4.247a.664.664 0 0 0-.002-.94.666.666 0 0 0-.942-.002L6 5.105 2.228 1.332z'/%3E%3C/g%3E%3C/svg%3E%0A");
				}
			}
		}
		.content {
			${tw`max-w-7xl mx-auto px-4 sm:px-6 md:px-8`}
			.ais-Hits {
				.ais-Hits-list {
					${tw`grid xl:grid-cols-4 sm:grid-cols-2 grid-cols-1 gap-10`}
					@media (max-width:1279px) and (min-width: 900px) {
						${tw`grid-cols-3`}
					}
				}
			}
		}
		footer {
			.ais-Pagination {
				${tw``}
				ul.ais-Pagination-list {
					${tw`flex items-center justify-center`}
					li.ais-Pagination-item {
						${tw`flex items-center justify-center text-gray-800 rounded h-10 w-10 bg-gray-200`}
						&.ais-Pagination-item--disabled {
							${tw`opacity-25`}
						}
						&.ais-Pagination-item--previousPage {
							${tw`mr-4 bg-transparent`}
						}
						&.ais-Pagination-item--nextPage {
							${tw`bg-transparent`}
							margin-left: calc(1rem - 4px);
						}
						.ais-Pagination-link {
							${tw`flex items-center justify-center h-full w-full`}
						}
						&.ais-Pagination-item--page {
							${tw`mr-4`}
						}
						&.ais-Pagination-item--selected {
							${tw`bg-yellow-500 font-bold`}
							a {
								${tw`text-gray-700`}
							}
						}
					}
				}
			}
		}
	}
`
const OffCanvasMenuButton = styled.div`
	${tw`md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3`}
	button {
		${tw`-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:bg-gray-200 transition ease-in-out duration-150`}
	}
`
const Mobile = styled.aside`
	@media (max-width: 899px) {
		button {
			${tw`fixed flex items-center justify-center bg-yellow-500 border-0 rounded-md text-yellow-900 text-sm font-bold cursor-pointer transform -translate-x-1/2 outline-none`}
			bottom: 2rem;
			box-shadow: 0 4px 22px 0 rgba(125, 85, 0, 0.6);
			&:focus {
				box-shadow: 0 4px 22px 0 rgba(185, 135, 0, 0.5);
			}
			left: 50%;
			min-height: 40px;
			min-width: 112px;
			svg {
				height: 14px;
				margin-right: 8px;
				width: 16px;
			}
		}
	}
`
const CantFind = tw.p`font-display text-base mb-4`
const FindDealer = styled(Link)`
	${tw`bg-yellow-500 hover:bg-yellow-400 text-gray-700 font-medium py-2 px-4 cursor-pointer rounded`}
`
const ShopHeading = styled.h1`
	${tw`max-w-7xl mx-auto px-4 sm:px-6 md:px-8 text-blue-500 text-3xl xl:text-4xl font-display font-bold mt-8 text-center`}
`

const ContentContainer = styled.div`
	${tw`bg-gray-50 mb-12 px-4 md:px-0`}

	> div {
		${tw`max-w-6xl mx-auto mt-12`}
		> h2 {
			${tw`text-3xl text-center text-blue-500 font-display font-bold`}
		}
		> h3 {
			${tw`text-2xl text-center text-blue-500 font-display font-bold`}
		}
		> h4 {
			${tw`text-xl text-center text-blue-500 font-display font-bold`}
		}

		> p {
			${tw`font-sans mt-8 text-center`}
		}

		> ul {
			${tw`mt-8 flex flex-col justify-center items-center`}

			> li {
				${tw`list-disc mt-2`}
			}
		}
	}
`
const WhichModel = styled(Link)`
	${tw`rounded font-bold bg-yellow-500 hover:bg-yellow-400 w-52 mx-auto block text-center py-3 mt-8`}
`
const LearnMore = styled(Link)`
	${tw`rounded font-bold bg-yellow-500 hover:bg-yellow-400 w-52 mx-auto block text-center py-3 mt-8`}
`

const Shop = props => {
	const [sidebarOpen, setSidebarOpen] = useState(false)
	useEffect(() => {
		const escHandler = async event => {
			if (event.keyCode === 27) {
				setSidebarOpen(false)
			}
		}
		document.addEventListener('keydown', escHandler, false)
		return () => {
			document.removeEventListener('keydown', escHandler, false)
		}
	})
	const containerRef = useRef(null)
	const headerRef = useRef(null)

	const openFilters = () => {
		setSidebarOpen(true)
		window.scrollTo(0, 0)
		window.addEventListener('keyup', onKeyUp)
		window.addEventListener('click', onClick)
	}

	const closeFilters = () => {
		setSidebarOpen(false)
		containerRef.current.scrollIntoView()
		window.removeEventListener('keyup', onKeyUp)
		window.removeEventListener('click', onClick)
	}

	function onKeyUp(event) {
		if (event.key !== 'Escape') {
			return
		}
		closeFilters()
	}

	function onClick(event) {
		if (event.target !== headerRef.current) {
			return
		}
		closeFilters()
	}
	const { page } = useStaticQuery(graphql`
		query shopPageQuery {
			page: allContentfulPage(filter: { slug: { eq: "shop" } }) {
				edges {
					node {
						slug
						seoTitle
						seoKeywords
						seoDescription
					}
				}
			}
		}
	`)
	const { slug, seoTitle, seoKeywords, seoDescription } = page.edges[0].node

	const [spare, setSpare] = useState(false)
	const win = props.searchState.query
	useEffect(() => {
		if (win != null || win != undefined) {
			if (win.indexOf('spare') != -1 || win.indexOf('parts') != -1) {
				setSpare(true)
			} else {
				setSpare(false)
			}
		}
	}, [win])

	return (
		<Layout>
			<Meta
				title={seoTitle}
				keywords={seoKeywords}
				description={seoDescription}
			/>
			<InstantSearch
				indexName="instant_search"
				searchClient={searchClient}
				searchState={props.searchState}
				createURL={props.createURL}
				onSearchStateChange={props.onSearchStateChange}
			>
				<SendHits />
				<Wrapper>
					<Header ref={headerRef}>
						{/* <p className="title">Find the right product, blazingly fast!</p> */}
						<SearchBox
							translations={{
								placeholder: 'Product, brand, color, …',
							}}
							submit={<Search size="20" />}
							reset={<X size="16" />}
						/>
						<span css={{ fontSize: '12px' }}>All prices inclusive of GST</span>
					</Header>

					<Configure
						attributesToSnippet={['description:50']}
						snippetEllipsisText="…"
						removeWordsIfNoResults="allOptional"
					/>

					<Dashboard ref={containerRef} data-show={sidebarOpen}>
						<Filters
							className="container-filters"
							data-show={sidebarOpen}
							onKeyUp={onKeyUp}
						>
							<div css={tw`h-0 flex-1 flex flex-col pt-5 pb-4 overflow-y-auto`}>
								<h2 css={tw`flex items-center flex-shrink-0 px-4`}>
									Filters
									<div className="clear-filters" data-layout="desktop">
										<ClearRefinements
											translations={{
												reset: (
													<>
														<RefreshCw size={14} />
														Reset
													</>
												),
											}}
										/>
									</div>
									<div className="clear-filters" data-layout="mobile">
										<ResultsNumberMobile />
									</div>
								</h2>
								<nav css={tw`mt-5 flex-1 px-2 bg-white`}>
									<h3 className="label" id="category-tree">
										<List aria-hidden />
										Categories
									</h3>
									<CategoryTree
										attributes={[
											'hierarchicalCategories.lvl0',
											'hierarchicalCategories.lvl1',
											'hierarchicalCategories.lvl2',
											'hierarchicalCategories.lvl3',
										]}
									/>
									<h3 className="label">
										<Tag aria-hidden />
										Brand
									</h3>
									<BrandSelect
										attribute="brand"
										searchable={false}
										operator="and"
									/>
									<h3 className="label">
										<Hash aria-hidden />
										Pool Type
									</h3>
									<AttributeSelect
										attribute="tags"
										searchable={false}
										operator="and"
									/>
									<h3 className="label">
										<DollarSign aria-hidden />
										Price
									</h3>
									{/* <div css={tw`mx-8 pt-2`}>
										<PriceSlider attribute="price" />
									</div> */}
								</nav>
							</div>
						</Filters>

						<FiltersFooter
							className="container-filters-footer"
							data-show={sidebarOpen}
							data-layout="mobile"
						>
							<div className="container-filters-footer-button-wrapper">
								<ClearFiltersMobile containerRef={containerRef} />
							</div>

							<div className="container-filters-footer-button-wrapper">
								<SaveFiltersMobile onClick={closeFilters} />
							</div>
						</FiltersFooter>

						<Board>
							<ShopHeading>
								Buy a Swimming Pool Cleaner &amp; Accessories
							</ShopHeading>
							<main tabIndex="0" onFocus={() => setSidebarOpen(false)}>
								<div
									className="menu-bar"
									css={tw`flex justify-end items-center`}
									data-layout="desktop"
								>
									<SortBy
										className="container-option"
										defaultRefinement="instant_search"
										items={[
											{
												label: 'Sort by featured',
												value: 'instant_search',
											},
											{
												label: 'Price ascending',
												value: 'instant_search_price_asc',
											},
											{
												label: 'Price descending',
												value: 'instant_search_price_desc',
											},
										]}
									/>

									<HitsPerPage
										className="container-option"
										items={[
											{
												label: '8 products per page',
												value: 8,
											},
											{
												label: '16 products per page',
												value: 16,
											},
											{
												label: '32 products per page',
												value: 32,
											},
										]}
										defaultRefinement={8}
									/>
								</div>

								<div className="content">
									<div css={tw`py-4`}>
										<Hits locale="en-AU" />
									</div>
								</div>
								{spare ? (
									<div className="content" css={tw`mb-8`}>
										<div css={tw`py-4`}>
											<CantFind>
												Can’t find what you’re looking for? Find your local
												spare parts dealer
											</CantFind>
											<FindDealer to={`/support/find-a-dealer`}>
												Find Spare Parts Dealer
											</FindDealer>
										</div>
									</div>
								) : (
									''
								)}
								<footer className="container-footer">
									<Pagination
										padding={2}
										showFirst={false}
										showLast={false}
										translations={{
											previous: <ChevronLeft size={20} />,
											next: <ChevronRight size={20} />,
										}}
									/>
								</footer>
								<ContentContainer>
									<div>
										<h2>
											Discover the benefits of a Maytronics Dolphin robotic
											swimming pool cleaner
										</h2>
										<p>
											Swimming pools are all about relaxation and enjoyment, which is why nobody wants to be burdened with the task of pool maintenance. Save yourself the countless hours of physical labour and frustration with a Dolphin swimming pool cleaner. It's the best piece of pool equipment you'll ever buy!
										</p>
										<p>
											Powerfully manufactured for the toughest of messes, our full range of robotic pool cleaners offer advanced technology and exceptional performance that you won’t find in most pool cleaners.
										</p>
										<h4 css={tw`mt-4`}>
											Your Maytronics Dolphin automatic pool cleaner can:
										</h4>
										<ul>
											<li>
												Adjust to all pool shapes, sizes and surface types
											</li>
											<li>
												Suck up loose debris on the pool floor and surface
											</li>
											<li>Scrub the pool floor and walls to remove grime</li>
											<li>Climb the walls and scrubs along the waterline</li>
										</ul>
										<p>
											No matter the size, shape or type of pool, nothing cleans like a Maytronics Dolphin. Our automatic pool cleaners work independently from the filter and pump, collecting debris and dirt in a bag or cartridge within the robot. Simply plug-in, press the power button and drop it in your pool for full cleaning coverage. You don’t even need to be home to start the cleaning process with the MyDolphin app.
										</p>
										<p>
											With our range of innovative swimming pool robots, you can spend less time cleaning your pool and more time enjoying it! Break out the pool toys for some family fun or relax with a refreshing dip in your crystal clear pool.

										</p>
									</div>
									<div>
										<h2>Not sure which model?</h2>
										<h4 css={tw`mt-4`}>
											Try our Dolphin Selector and find the right swimming pool cleaner for you!
										</h4>
										<p>We pride ourselves on our efficient customer service with 
											Australia wide delivery for the complete automatic pool 
											cleaner range from Maytronics.</p>
										<WhichModel to="/which-model">WHICH MODEL</WhichModel>
									</div>
									<div>
									<img
											src="https://images.ctfassets.net/ftcnv5up9dxv/4yPYIAQVPtfuTdZa7kLEiS/436dd83a79185fe4de6e2c667375fab4/Liberty400_HP_D1_250x250.jpg"
											alt="dolphin liberty 400 cordless"
											tw="mx-auto mt-8"
										/>
									<h2>Explore our Range of Cordless Pool Cleaners</h2>
									<p>Keeping your pool sparkling clean has never been easier with the new Dolphin Liberty cordless pool cleaner! Offering the same unmatched Dolphin performance without the hassle of tangled cords, this innovative pool cleaner is designed to solve complex just pool cleaning problems that other models can miss.</p>
									<ul>
											<li>
											Cordless design for ultimate agility
											</li>
											<li>
											A whole week’s cleaning on one charge
											</li>
											<li>Climbs up to you for easy removal from the pool</li>
											<li>Magnetic connect for inductive charging</li>
										</ul>
										<p>Our new generation of cordless robotic pool cleaners from Maytronics will transform the way you care for your swimming pool. Discover for yourself why the Dolphin Liberty is an effective cleaning solution for any pool type.</p>
										<LearnMore to="/liberty-cordless">LEARN MORE</LearnMore>
									</div>
									<div>
										<h2>Climate Care Certified Dolphin Pool Cleaner</h2>

										<img
											src="https://images.ctfassets.net/ftcnv5up9dxv/2kM27T7VEUB9tMwH2Kn5QK/9208f9d704c51ee30293e6f91358f9d1/climate-care.jpg?w=250&h=250&fm=jpg&fl=progressive&q=100"
											alt="climate care certified"
											tw="mx-auto mt-8"
										/>
									</div>
									<div>
										<h3>
											The leader in robotic swimming pool cleaning technology
										</h3>
										<p>
											Dolphin is the new benchmark in environmental
											sustainability as the world’s first climate care certified
											automatic swimming pool cleaner. The Climate Care Certification 
											Program is an initiative of the Swimming Pool &amp; Spa Association 
											of Australia Ltd (SPASA Australia). It is the industry’s efficiency 
											and sustainability certification program.
										</p>	
											
										<p>
											Our range of robotic swimming pool
											cleaners are proven to reduce energy consumption by up to
											83% when compared to other traditional suction pool cleaners
											and pressure pool cleaners. That could save you up to $350
											per year in energy costs while still keeping your pool
											sparkling clean.{' '}</p>	
											
										<p>
											By using a Dolphin robot for 2 to 3 hours a day you can
											dramatically reduce the load on your pool’s filtration
											system, and your total water usage for backwashes shrinks
											by 60-80%. You can also use fewer chemicals for your pool,
											simply because you don’t use up as much water backwashing
											to waste.
										</p>
										<p>
											Whether you have an in ground or above ground pool,
											Maytronics Dolphin swimming pool cleaners are the
											environmentally friendly way to enjoy crystal clear
											water.  Browse the full Dolphin range in our shop now and 
											discover what makes Maytronics one of Australia's most popular 
											brands for swimming pool cleaners.
										</p>
									</div>
								</ContentContainer>
							</main>
						</Board>
					</Dashboard>
					<Mobile id="mobile-menu" data-layout="mobile">
						<button
							className="filters-button"
							data-action="open-overlay"
							onClick={openFilters}
							data-show={sidebarOpen}
						>
							<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 14">
								<path
									d="M15 1H1l5.6 6.3v4.37L9.4 13V7.3z"
									stroke="currentColor"
									strokeWidth="1.29"
									fill="none"
									fillRule="evenodd"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
							Filters
						</button>
					</Mobile>
				</Wrapper>
			</InstantSearch>
		</Layout>
	)
}

export const SendHits = connectHits(({ hits }) => {
	useEffect(() => {
		let isFresh = true
		/**
		 * Google Analytics - Tag Manager - Enhanced Ecommerce
		 * Measuring product impressions for every search result change
		 * */
		const isBrowser = typeof window !== 'undefined'
		const dataLayer = isBrowser && window.dataLayer
		const getEvents = dataLayer.filter(
			data => data.event === 'productImpression'
		)
		// console.log('getEvents', getEvents)
		const oldHits = getEvents[getEvents.length - 1]?.ecommerce?.impressions
		// console.log('oldHits', oldHits)
		const newHits = hits.map(hit => ({
			name: hit.name, // the name of the product, including Title and Subtitle
			id: hit.sku, // product serial number
			price: hit.price, // actual price per unit or in case of fixed price discounts the discounted price
			brand: hit.brand, // product brand
			category: hit.categories.toString(), // Dolphin domestic, Dolphin commercial, mineral swim, ozone swim
			list: 'Category List', // the type of list for the current product impression )e.g. Category List, Search Results etc.)
			position: hit.__position, // the position of the product within the list
		}))
		// console.log('newHits', newHits)
		const hasArray = newHits?.length >= 1
		isFresh =
			(newHits && newHits.length >= 1 && newHits[newHits.length - 1].id) !==
			(oldHits && oldHits.length >= 1 && oldHits[oldHits.length - 1].id)
		if (!!isBrowser && !!dataLayer && hasArray && isFresh) {
			// console.log('send to dataLayer', isFresh)
			dataLayer.push({
				event: 'productImpression',
				ecommerce: {
					currencyCode: 'AUD',
					impressions: newHits,
				},
			})
		}
		return () => {
			isFresh = false
		}
	}, [hits])
	return <></>
})

export default withURLSync(Shop)
